import { useCallback, useEffect, useRef, useState } from "react";
import { VoiceRecorder } from "capacitor-voice-recorder";

export const MAX_RECORDING_MS = 120_000;

export interface RecordedAudio {
  blob: Blob;
  mimeType: string;
  durationMs: number;
  autoStopped: boolean;
}

export type StartResult = "started" | "permission_denied" | "unsupported";

interface UseAudioRecorderOptions {
  onAutoStop?: (audio: RecordedAudio) => void;
}

const base64ToBlob = async (base64: string, mimeType: string): Promise<Blob> => {
  const dataUrl = `data:${mimeType};base64,${base64}`;
  const res = await fetch(dataUrl);
  return res.blob();
};

export const useAudioRecorder = ({ onAutoStop }: UseAudioRecorderOptions = {}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [durationMs, setDurationMs] = useState(0);

  const startedAtRef = useRef<number | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onAutoStopRef = useRef(onAutoStop);
  onAutoStopRef.current = onAutoStop;

  const clearTimers = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    clearTimers();
    startedAtRef.current = null;
    setIsRecording(false);
    setDurationMs(0);
  }, [clearTimers]);

  const stopInternal = useCallback(
    async (autoStopped: boolean): Promise<RecordedAudio | null> => {
      clearTimers();
      const startedAt = startedAtRef.current;
      try {
        const result = await VoiceRecorder.stopRecording();
        const data = result.value;
        if (!data.recordDataBase64) return null;
        const blob = await base64ToBlob(data.recordDataBase64, data.mimeType);
        const elapsed =
          typeof data.msDuration === "number" && data.msDuration > 0
            ? data.msDuration
            : startedAt
              ? Date.now() - startedAt
              : 0;
        return { blob, mimeType: data.mimeType, durationMs: elapsed, autoStopped };
      } catch {
        return null;
      } finally {
        resetState();
      }
    },
    [clearTimers, resetState],
  );

  const start = useCallback(async (): Promise<StartResult> => {
    const supported = await VoiceRecorder.canDeviceVoiceRecord().catch(() => ({ value: false }));
    if (!supported.value) return "unsupported";

    const has = await VoiceRecorder.hasAudioRecordingPermission().catch(() => ({ value: false }));
    if (!has.value) {
      const granted = await VoiceRecorder.requestAudioRecordingPermission().catch(() => ({
        value: false,
      }));
      if (!granted.value) return "permission_denied";
    }

    try {
      await VoiceRecorder.startRecording();
    } catch {
      return "unsupported";
    }

    startedAtRef.current = Date.now();
    setIsRecording(true);
    setDurationMs(0);

    tickRef.current = setInterval(() => {
      if (startedAtRef.current) {
        setDurationMs(Date.now() - startedAtRef.current);
      }
    }, 200);

    autoStopRef.current = setTimeout(async () => {
      const audio = await stopInternal(true);
      if (audio && onAutoStopRef.current) {
        onAutoStopRef.current(audio);
      }
    }, MAX_RECORDING_MS);

    return "started";
  }, [stopInternal]);

  const stop = useCallback(
    (): Promise<RecordedAudio | null> => stopInternal(false),
    [stopInternal],
  );

  const cancel = useCallback(async (): Promise<void> => {
    clearTimers();
    try {
      await VoiceRecorder.stopRecording();
    } catch {
      // ignore — recorder may already be stopped
    }
    resetState();
  }, [clearTimers, resetState]);

  useEffect(() => {
    return () => {
      if (tickRef.current || autoStopRef.current) {
        clearTimers();
        VoiceRecorder.stopRecording().catch(() => undefined);
      }
    };
  }, [clearTimers]);

  return { isRecording, durationMs, start, stop, cancel };
};
